import { Avatar, Box, Button, CircularProgress, Dialog, IconButton, Typography, useTheme } from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import dayjs from 'dayjs'

import { FLAT_COLORS, getNameInitials, Icons, USER_CARD_COLOR_SETS } from '../../utils/utilities'
import { useOrganizationStore } from '../../store/organization.store'
import selectInstituteImg from '../../assets/selectinstitute.svg'
import TryAgainMessageBlock from '../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../common/DataLoadingSpinner'
import simbaImg from '../../assets/simba-ai/celebration.png'
import CustomTable from '../common/custom-table/CustomTable'
import { useSimbaAIStore } from '../../store/simbaai.store'
import leaderboardImg from '../../assets/leaderboard.svg'
import DepartmentSelect from '../audit/DepartmentSelect'
import InstituteSelect from '../audit/InstituteSelect'
import { useUserStore } from '../../store/user.store'
import CustomTooltip from '../common/CustomTooltip'
import api from '../../service/api'

const RankCell = ( { rankDetails } ) => {
    return (
        <Box display="flex" justifyContent="center" marginLeft="-10px" alignItems="center" gap="10px">
            {rankDetails.rank === 1 && <Typography color='gold' fontSize="18px"  >{Icons.default.EmojiEvents}</Typography>}
            {rankDetails.rank === 2 && <Typography color='silver' fontSize="18px"  >{Icons.default.EmojiEvents}</Typography>}
            {rankDetails.rank === 3 && <Typography color='brown' fontSize="18px"  >{Icons.default.EmojiEvents}</Typography>}
            {rankDetails.rank > 3 && <Typography color='GrayText' fontSize="18px" marginLeft="5px" marginRight="5px" >-</Typography>}
            <Typography fontSize="14px">{rankDetails.rank}</Typography>
        </Box>
    )
}
const NameCell = ( { rankDetails, index } ) => {

    const { palette } = useTheme()

    return (
        <Box display="flex" marginLeft="-10px" alignItems="center" gap="10px">
            <Avatar sx={{ color: palette.common.white, textTransform: "uppercase", fontSize: "14px", width: "25px", height: "25px", bgcolor: USER_CARD_COLOR_SETS[index % USER_CARD_COLOR_SETS.length].primary }} >
                {getNameInitials( rankDetails.student_name )}
            </Avatar>
            <Typography fontSize="14px">{rankDetails.student_name}</Typography>
        </Box>
    )
}

const columns = [
    { field: "rank", name: "Rank", align: "center", dataType: "number", width: "100px", renderCell: row => <RankCell rankDetails={row} /> },
    { field: "rank_score", name: "Points", align: "center", dataType: "number", width: "100px" },
    { field: "student_name", name: "Name", width: "230px", renderCell: ( row, index ) => <NameCell rankDetails={row} index={index} /> },
    { field: "student_id", name: "AUID", width: "150px" },
    { field: "attempt_count", align: "center", name: "Attempts", width: "150px" },
    { field: "actual_average", align: "center", name: "Average Score", width: "150px" },
]

const LeaderboardDialog = observer( ( { open, setOpen } ) => {

    const [selectedMonth, setSelectedMonth] = useState( dayjs().format( "YYYY-MM" ) )
    const [loadingDepartments, setLoadingDepartments] = useState( false )
    const [selectedDepartment, setSelectedDepartment] = useState( null )
    const [showMonthPicker, setShowMonthPicker] = useState( false )
    const [departments, setDepartments] = useState( [] )

    const OrganizationStore = useOrganizationStore()
    const SimbaAIStore = useSimbaAIStore()
    const UserStore = useUserStore()

    const [selectedInstitute, setSelectedInstitute] = useState( ["PRINCIPAL"].includes( UserStore.getUser.user_role ) ? UserStore.getUser.user_org_code : null )

    const { palette, layout } = useTheme()

    const monthPickerButtonRef = useRef()

    const leaderboard = SimbaAIStore.getLeaderboardData[selectedMonth] || []
    const error = SimbaAIStore.getErrors.leaderboard[selectedMonth]
    const { initialLoad: loading, refreshing } = SimbaAIStore.getLeaderboardLoadingStatus

    const handleDepartmentChange = ( ids ) => {
        const departmentId = ids.split( "-" ).at( 0 )
        setSelectedDepartment( ids )
        fetchLeaderBoard( { month: selectedMonth, department_id: departmentId } )
    }

    const handleSelectedSeasonChange = val => {
        if ( val && val?.isValid() ) {
            setSelectedMonth( dayjs( val ).format( "YYYY-MM" ) )
            setShowMonthPicker( false )
        }
    }

    const fetchLeaderBoard = useCallback( ( options = {} ) => {
        SimbaAIStore.fetchLeaderboard( { month: selectedMonth, ...options } )
    }, [SimbaAIStore, selectedMonth] )

    useEffect( () => {
        if ( open && !["ADMIN", "PRINCIPAL", "SUBADMIN"].includes( UserStore.getUser.user_role ) )
            fetchLeaderBoard()
    }, [fetchLeaderBoard, open, UserStore] )



    useEffect( () => {
        const getData = async () => {
            setDepartments( [] )
            try {
                if ( "PRINCIPAL" === UserStore.getUser.user_role || UserStore.getUser.user_auditor || ["ADMIN", "SUBADMIN"].includes( UserStore.getUser?.user_role?.toUpperCase() ) ) {
                    setLoadingDepartments( true )
                    const institute = selectedInstitute || ""
                    if ( ( UserStore.getUser.user_auditor || ["ADMIN", "SUBADMIN"].includes( UserStore.getUser?.user_role?.toUpperCase() ) ) && ( !OrganizationStore.getOrganization || OrganizationStore.getOrganization?.length === 0 ) ) {
                        if ( OrganizationStore.getOrganization?.length === 0 )
                            await OrganizationStore.fetchOrganization()
                        setLoadingDepartments( false )
                    }

                    let fetchDepartments = true
                    if ( "PRINCIPAL" !== UserStore.getUser.user_role && !institute )
                        fetchDepartments = false
                    if ( fetchDepartments === true ) {
                        const { data: { data } } = await api.audit.fetchInstituteDepartment( { institute } )
                        setDepartments( data )
                    }
                }
            } catch ( err ) {
                toast( err?.response?.data?.message || "Something went wrong while fetching the institute's department." )
            } finally {
                setLoadingDepartments( false )
            }
        }
        getData()
    }, [UserStore, selectedInstitute, OrganizationStore] )


    return (
        <Dialog slotProps={{
            paper: {
                sx: {
                    display: "flex",
                    flexDirection: "column",
                    overflow: "auto",
                }
            }
        }} open={open} onClose={() => setOpen( false )} maxWidth="md">

            <Box
                sx={{
                    background: `linear-gradient(156deg, ${palette.primary.main + "ee"}, ${palette.primary.main + "88"})`
                }}
                padding="10px 20px"
            >
                <Box
                    gap="10px"
                    justifyContent="space-between"
                    display="flex"
                    alignItems="flex-start"
                >
                    <Box display="flex" gap="20px" alignItems="center">
                        <Box component="img" src={simbaImg} height="80px" />
                        <Box>
                            <Typography color={palette.common.white} variant='h4'>Practice Sessions Leaderboard</Typography>
                            {UserStore.getUser.user_role === "STUDENT" && <Typography fontSize="14px" color={palette.grey[200]}>Season: {selectedMonth}, Group: {UserStore.getUser.ac_year}, Section {UserStore.getUser.section_short_name} </Typography>}
                            {["FACULTY", "HOD"].includes( UserStore.getUser.user_role ) && <Typography fontSize="14px" color={palette.grey[200]}>Season: {selectedMonth}, Department: {UserStore.getUser.user_dept_code} </Typography>}
                            {["ADMIN", "SUBADMIN", "PRINCIPAL"].includes( UserStore.getUser.user_role ) && <Typography fontSize="14px" color={palette.grey[200]}>Season: {selectedMonth} </Typography>}
                        </Box>
                    </Box>
                    <IconButton sx={{ color: palette.common.white }} onClick={() => setOpen( false )}>
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>
                <Box display="flex" gap="10px" marginTop="10px">

                    <Button
                        ref={monthPickerButtonRef}
                        size="small"
                        sx={{
                            textTransform: "capitalize",
                            padding: "4.5px 10px",
                            cursor: "pointer",
                            background: palette.common.white,
                            color: palette.common.black,
                            borderRadius: "20px"
                        }}
                        value="-"
                        endIcon={Icons.default.KeyboardArrowDownIcon}
                        selected={Boolean( selectedMonth.match( /^(\d{4}-\d{2})$/ ) )}
                        onClick={() => setShowMonthPicker( true )}   // <-- OPEN PICKER
                    >
                        Select season: {selectedMonth}
                    </Button>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            open={showMonthPicker}
                            onClose={() => setShowMonthPicker( false )}
                            views={["year", "month"]}
                            onMonthChange={handleSelectedSeasonChange}
                            slotProps={{
                                textField: {
                                    component: "div",
                                    sx: {
                                        display: "none"      // <-- COMPLETELY HIDE INPUT
                                    }
                                },
                                popper: {
                                    anchorEl: monthPickerButtonRef.current, // ⬅ CUSTOM ANCHOR!
                                    placement: "bottom-start"
                                }
                            }}
                            slots={{
                                openPickerButton: null  // <-- HIDE CALENDAR ICON BUTTON
                            }}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                opacity: 0,
                                pointerEvents: "none"   // <-- SO IT DOESN’T BLOCK CLICKS
                            }}
                        />
                    </LocalizationProvider>

                    {["PRINCIPAL", "ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && <Box display="flex" gap={layout.cardsGap}>
                        {["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && <InstituteSelect
                            buttonStyles={{
                                background: palette.common.white,
                                color: palette.common.black,
                                borderRadius: "20px",
                                disabled: {
                                    background: palette.grey[300],
                                    color: palette.grey[800]
                                }
                            }}
                            disabled={["PRINCIPAL"].includes( UserStore.getUser.user_role )} selectedInstitute={selectedInstitute || ""}
                            showAll={false}
                            handleInstituteChange={setSelectedInstitute}
                        />}
                        <DepartmentSelect
                            loading={loadingDepartments}
                            buttonStyles={{
                                background: palette.common.white,
                                color: palette.common.black,
                                borderRadius: "20px",
                                disabled: {
                                    background: palette.grey[300],
                                    color: palette.grey[800]
                                }
                            }}
                            departments={departments}
                            disabled={!selectedInstitute || loadingDepartments}
                            selectedDepartment={selectedDepartment}
                            handleDepartmentChange={handleDepartmentChange}
                        />
                    </Box>}
                </Box>
            </Box>


            {loading && <DataLoadingSpinner waitingMessage="Loading leaderboard data" />}

            {error && <TryAgainMessageBlock getData={fetchLeaderBoard} err={error.message} code={error.code} loading={refreshing} />}

            {leaderboard.length > 0 && !error && !loading && <Box flexGrow={1} overflow="auto" display='flex' position="relative" flexDirection="column">

                {refreshing && <Box
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        inset: 0,
                        background: palette.grey[500] + '55',
                        zIndex: 10
                    }}
                >
                    <CircularProgress />
                </Box>}

                {["FACULTY", "HOD", "STUDENT"].includes( UserStore.getUser.user_role ) && <CustomTable columns={columns} data={leaderboard || []} />}
                {["PRINCIPAL", "ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && selectedDepartment && <CustomTable columns={columns} data={leaderboard || []} />}
                {["PRINCIPAL", "ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && !selectedDepartment && <Box padding="10px" display="flex" flexDirection="column" alignItems="center" gap="10px" justifyContent="center" margin="10px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} >
                    <Box component="img" src={selectInstituteImg} height="140px" />
                    <Typography variant='subtitle2'>Select department to view leaderboard details.</Typography>
                </Box>}


                <Box
                    sx={{
                        background: palette.primary.main
                    }}
                    display="flex"
                    alignItems="center"
                    padding="10px 20px"
                    justifyContent="space-between"
                >
                    <Box>
                        <CustomTooltip bg={palette.action.activatedOpacity} title={
                            <Box>
                                <Typography variant='subtitle2' display="block" gutterBottom>How ranks are calculated?</Typography>
                                <Typography variant='caption' component="p">
                                    Points = (v*A + m*C) / (v + m)<br />
                                    v = Individual student's total attempts.<br />
                                    A = Individual student's actual average score.<br />
                                    m = The "Threshold" (Section Average Attempts). <br />
                                    C = The "Baseline" (Section Average Score across all attempts).<br />
                                </Typography>
                            </Box>
                        } >
                            <IconButton sx={{ color: palette.common.white }} size='small'>{Icons.default.Info}</IconButton>
                        </CustomTooltip>
                    </Box>
                    <Box>
                        <Typography color={palette.common.white} variant='caption'>{leaderboard.length} participants</Typography>
                    </Box>
                </Box>
            </Box>}

            {!refreshing && !error && leaderboard.length === 0 && <Box padding="20px" display="flex" flexDirection="column" gap="20px" alignItems="center" justifyContent="center">
                <Box component="img" src={leaderboardImg} height="180px" alt="Leaderboard" />
                <Typography variant='subtitle2'>No data to show!</Typography>
            </Box>}

        </Dialog>
    )
} )

export default LeaderboardDialog
